import { z } from 'zod';
/**
 * Personal trainer (P1, estudo de 23/10, aprovado em 24/10). A ficha de treino segue o plano
 * alimentar: modelo, rascunho e publicado (mudar o publicado é duplicar numa versão nova), PDF
 * com o cabeçalho da clínica e o CREF de quem assina, e o publicado vai para a Saúde do aluno,
 * onde ele registra carga, repetições e RIR por série, também sem internet.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const isoDateTime = z.iso.datetime();
// ── Biblioteca de exercícios ───────────────────────────────────────────────
export const EXERCISE_MUSCLES = [
    'abdominals',
    'abductors',
    'adductors',
    'biceps',
    'calves',
    'chest',
    'forearms',
    'glutes',
    'hamstrings',
    'lats',
    'lower back',
    'middle back',
    'neck',
    'quadriceps',
    'shoulders',
    'traps',
    'triceps',
];
export const exerciseMuscleSchema = z.enum(EXERCISE_MUSCLES);
export const EXERCISE_MUSCLE_LABEL = {
    abdominals: 'Abdômen',
    abductors: 'Abdutores',
    adductors: 'Adutores',
    biceps: 'Bíceps',
    calves: 'Panturrilhas',
    chest: 'Peitoral',
    forearms: 'Antebraços',
    glutes: 'Glúteos',
    hamstrings: 'Posteriores de coxa',
    lats: 'Dorsais',
    'lower back': 'Lombar',
    'middle back': 'Meio das costas',
    neck: 'Pescoço',
    quadriceps: 'Quadríceps',
    shoulders: 'Ombros',
    traps: 'Trapézio',
    triceps: 'Tríceps',
};
export const EXERCISE_EQUIPMENT = [
    'barbell',
    'dumbbell',
    'cable',
    'machine',
    'kettlebells',
    'bands',
    'body only',
    'e-z curl bar',
    'medicine ball',
    'exercise ball',
    'foam roll',
    'other',
];
export const exerciseEquipmentSchema = z.enum(EXERCISE_EQUIPMENT);
export const EXERCISE_EQUIPMENT_LABEL = {
    barbell: 'Barra',
    dumbbell: 'Halteres',
    cable: 'Polia',
    machine: 'Máquina',
    kettlebells: 'Kettlebell',
    bands: 'Elástico',
    'body only': 'Peso do corpo',
    'e-z curl bar': 'Barra W',
    'medicine ball': 'Medicine ball',
    'exercise ball': 'Bola suíça',
    'foam roll': 'Rolo de liberação',
    other: 'Outros',
};
export const EXERCISE_CATEGORIES = [
    'strength',
    'stretching',
    'plyometrics',
    'cardio',
    'powerlifting',
    'olympic weightlifting',
    'strongman',
];
export const exerciseCategorySchema = z.enum(EXERCISE_CATEGORIES);
export const EXERCISE_CATEGORY_LABEL = {
    strength: 'Força',
    stretching: 'Alongamento e mobilidade',
    plyometrics: 'Pliometria',
    cardio: 'Cardio',
    powerlifting: 'Powerlifting',
    'olympic weightlifting': 'Levantamento olímpico',
    strongman: 'Strongman',
};
export const EXERCISE_LEVEL_LABEL = {
    beginner: 'Iniciante',
    intermediate: 'Intermediário',
    expert: 'Avançado',
};
/** Um exercício da biblioteca: o da base aberta (`fedb:`) ou o criado pela clínica (`meu:`). */
export const trainingExerciseSchema = z.object({
    id: z.string().min(1).max(100),
    name: z.string(),
    primary: z.array(exerciseMuscleSchema),
    secondary: z.array(exerciseMuscleSchema),
    equipment: exerciseEquipmentSchema.nullable(),
    category: exerciseCategorySchema,
    level: z.enum(['beginner', 'intermediate', 'expert']),
    /** Composto (várias articulações) ou isolado. */
    mechanic: z.enum(['compound', 'isolation']).nullable(),
    /** Passos de execução, quando há tradução revisada (a base aberta vem em inglês). */
    steps: z.array(z.string()),
    /** Vídeo do próprio personal (link), só nos exercícios da clínica. */
    videoUrl: z.string().nullable(),
    own: z.boolean(),
});
export const trainingExerciseSearchSchema = z.object({
    q: z.string().trim().max(80).optional(),
    muscle: exerciseMuscleSchema.optional(),
    equipment: exerciseEquipmentSchema.optional(),
    limit: z.coerce.number().int().min(1).max(60).default(30),
});
export const trainingExerciseSearchResultSchema = z.object({
    exercises: z.array(trainingExerciseSchema),
    total: z.number().int(),
    attribution: z.string(),
});
// ── Ficha de treino ────────────────────────────────────────────────────────
export const WORKOUT_LIMITS = { sheets: 7, items: 20, note: 400, guidance: 1200 };
/** Métodos do dia a dia; bi-set e tri-set juntam itens seguidos pelo mesmo `group`. */
export const WORKOUT_METHODS = [
    'normal',
    'biset',
    'triset',
    'dropset',
    'restpause',
    'piramide',
    'circuito',
];
export const workoutMethodSchema = z.enum(WORKOUT_METHODS);
export const WORKOUT_METHOD_LABEL = {
    normal: 'Normal',
    biset: 'Bi-set',
    triset: 'Tri-set',
    dropset: 'Drop-set',
    restpause: 'Rest-pause',
    piramide: 'Pirâmide',
    circuito: 'Circuito',
};
export const workoutItemSchema = z.object({
    id: z.string().min(1).max(40),
    /** Da biblioteca; nulo quando o personal escreveu um exercício solto. */
    exerciseId: z.string().max(100).nullable(),
    /** O nome na hora da prescrição: a ficha publicada não muda se a biblioteca mudar. */
    name: z.string().trim().min(1).max(100),
    primary: z.array(exerciseMuscleSchema).max(6),
    sets: z.number().int().min(1).max(12),
    /** "10", "10-12", "até a falha", "30 s". */
    reps: z.string().trim().min(1).max(24),
    /** Carga sugerida em kg (o aluno ajusta no registro). */
    loadKg: z.number().min(0).max(1000).nullable(),
    restSec: z.number().int().min(0).max(600),
    /** Repetições na reserva no fim da série (0 = falha). */
    rir: z.number().int().min(0).max(6).nullable(),
    method: workoutMethodSchema,
    /** Itens seguidos com o mesmo grupo formam o bi-set, o tri-set ou o circuito. */
    group: z.string().max(8).nullable(),
    /** Ex.: "3-0-1-0". */
    cadence: z.string().max(12).nullable(),
    note: z.string().max(WORKOUT_LIMITS.note),
});
export const workoutSheetSchema = z.object({
    id: z.string().min(1).max(40),
    /** "A", "B", "C"… */
    label: z.string().trim().min(1).max(12),
    /** "Costas e bíceps". */
    focus: z.string().max(60),
    items: z.array(workoutItemSchema).max(WORKOUT_LIMITS.items),
});
export const WORKOUT_GOALS = [
    'hipertrofia',
    'forca',
    'emagrecimento',
    'condicionamento',
    'resistencia',
    'reabilitacao',
    'mobilidade',
];
export const workoutGoalSchema = z.enum(WORKOUT_GOALS);
export const WORKOUT_GOAL_LABEL = {
    hipertrofia: 'Hipertrofia',
    forca: 'Força',
    emagrecimento: 'Emagrecimento',
    condicionamento: 'Condicionamento',
    resistencia: 'Resistência',
    reabilitacao: 'Volta ao treino',
    mobilidade: 'Mobilidade',
};
export const workoutContentSchema = z.object({
    sheets: z.array(workoutSheetSchema).min(1).max(WORKOUT_LIMITS.sheets),
    goal: workoutGoalSchema.nullable(),
    /** Ciclo de semanas (mesociclo): quantas e em qual o aluno está. */
    weeks: z.number().int().min(1).max(24).nullable(),
    /** Vezes por semana combinadas. */
    perWeek: z.number().int().min(1).max(7).nullable(),
    guidance: z.string().max(WORKOUT_LIMITS.guidance),
    /** Modalidade do atendimento (Res. CONFEF 542/2024, art. 7º); sem o campo, presencial. */
    delivery: z.enum(['presencial', 'distancia', 'hibrido']).optional(),
});
export const WORKOUT_DELIVERY_LABEL = {
    presencial: 'Presencial',
    distancia: 'A distância, pelo aplicativo',
    hibrido: 'Presencial e a distância',
};
/**
 * `programado`: enviada e assinada, mas começa depois (a troca do fim do ciclo, P2). No dia do
 * começo vira `publicado` e a anterior vai para o arquivo.
 */
export const WORKOUT_STATUSES = ['rascunho', 'publicado', 'programado', 'arquivado'];
export const workoutStatusSchema = z.enum(WORKOUT_STATUSES);
export const workoutPlanSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid().nullable(),
    template: z.boolean(),
    title: z.string(),
    status: workoutStatusSchema,
    startsOn: isoDate.nullable(),
    /** A ficha tem prazo (no atendimento a distância, até 30 dias: Res. CONFEF 542/2024, art. 4º). */
    validUntil: isoDate.nullable(),
    content: workoutContentSchema,
    author: z.string(),
    /** CREF de quem assinou ao publicar. */
    authorRegistration: z.string().nullable(),
    updatedAt: isoDateTime,
    publishedAt: isoDateTime.nullable(),
});
export const workoutPlanSummarySchema = workoutPlanSchema.omit({ content: true }).extend({
    sheets: z.number().int(),
    exercises: z.number().int(),
});
export const saveWorkoutPlanSchema = z.object({
    title: z.string().trim().min(1).max(80),
    startsOn: isoDate.nullable().optional(),
    validUntil: isoDate.nullable().optional(),
    content: workoutContentSchema,
});
export const createWorkoutPlanSchema = saveWorkoutPlanSchema
    .partial({ content: true, title: true })
    .extend({
    fromId: z.uuid().optional(),
    patientId: z.uuid().nullable().optional(),
});
/** Aplicar um modelo a vários alunos de uma vez (o elogio nº 1 dos concorrentes: montar rápido). */
export const applyWorkoutTemplateSchema = z.object({
    patientIds: z.array(z.uuid()).min(1).max(50),
    startsOn: isoDate.nullable().optional(),
});
// ── Saúde: o treino do lado do aluno ───────────────────────────────────────
export const workoutSetLogSchema = z.object({
    itemId: z.string().min(1).max(40),
    /** 1, 2, 3… */
    set: z.number().int().min(1).max(12),
    kg: z.number().min(0).max(1000).nullable(),
    reps: z.number().int().min(0).max(200).nullable(),
    rir: z.number().int().min(0).max(6).nullable(),
});
/**
 * Um treino feito. `clientId` nasce no aparelho: o mesmo treino enviado de novo (sem internet,
 * fila que repete) não vira dois.
 */
export const workoutSessionInputSchema = z.object({
    clientId: z.string().min(8).max(60),
    planId: z.uuid(),
    sheetId: z.string().min(1).max(40),
    startedAt: isoDateTime,
    finishedAt: isoDateTime,
    sets: z.array(workoutSetLogSchema).max(240),
    /** Como foi: 1 (muito fácil) a 10 (máximo). */
    effort: z.number().int().min(1).max(10).nullable(),
    note: z.string().max(400),
});
export const workoutSessionSchema = workoutSessionInputSchema.extend({ id: z.uuid() });
/**
 * Lembrete de treino do aluno (P1): ele escolhe os dias (0 domingo … 6 sábado) e o horário; o
 * aviso chega com "Bora treinar" e "Hoje não". Não chega se ele já treinou no dia.
 */
export const workoutReminderSchema = z.object({
    enabled: z.boolean(),
    days: z.array(z.number().int().min(0).max(6)).max(7),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
export const patientWorkoutSchema = z.object({
    plan: workoutPlanSchema.nullable(),
    /** A próxima ficha na ordem (depois da última feita). */
    nextSheetId: z.string().nullable(),
    /** Última vez de cada item: carga e repetições da melhor série. */
    last: z.record(z.string(), z.object({ kg: z.number().nullable(), reps: z.number().nullable() })),
    sessions: z.array(workoutSessionSchema.pick({ id: true, sheetId: true, finishedAt: true })),
    clinicName: z.string(),
    /** O lembrete que o aluno ligou (nulo: nunca ligou). */
    reminder: workoutReminderSchema.nullable().optional(),
    /** O CREF que assinou a ficha foi conferido pelo Secretário no site do conselho. */
    authorChecked: z.boolean().optional(),
    /** A ficha do próximo ciclo, já enviada, e o dia em que ela entra. */
    upcoming: z.object({ title: z.string(), startsOn: isoDate }).nullable().optional(),
    /** Dias com treino nesta semana (desde segunda), no app ou com presença no estúdio. */
    weekDone: z.number().int().optional(),
    /** O último dia com treino (no app ou presença). */
    lastDay: isoDate.nullable().optional(),
});
/** Quantos exercícios a ficha tem, contando todas as letras. */
export function workoutExerciseCount(content) {
    return content.sheets.reduce((sum, sheet) => sum + sheet.items.length, 0);
}
// ── CREF ────────────────────────────────────────────────────────────────────
/**
 * O CREF no formato do conselho: seis dígitos, a categoria (G graduado, P provisionado) e a UF
 * do conselho regional (ex.: 012345-G/ES). Aceita como a pessoa digita ("cref 12345 g es").
 */
const CREF_UFS = [
    'AC',
    'AL',
    'AM',
    'AP',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MG',
    'MS',
    'MT',
    'PA',
    'PB',
    'PE',
    'PI',
    'PR',
    'RJ',
    'RN',
    'RO',
    'RR',
    'RS',
    'SC',
    'SE',
    'SP',
    'TO',
];
export function normalizeCref(text) {
    const compact = text
        .toUpperCase()
        .replace(/CREF/g, '')
        .replace(/[^0-9A-Z]+/g, '');
    const match = /^(\d{1,6})([GP])([A-Z]{2})$/.exec(compact);
    if (!match)
        return null;
    const [, digits, category, uf] = match;
    if (!CREF_UFS.includes(uf))
        return null;
    return `CREF ${digits.padStart(6, '0')}-${category}/${uf}`;
}
export const publishWorkoutSchema = z.object({
    /** CREF de quem assina; sem ele a ficha não é publicada (Lei 9.696/1998). */
    registration: z.string().trim().min(5).max(40),
});
// ── Acompanhamento (P2): quem precisa de ficha nova e quem sumiu ────────────
/** A ficha avisa o personal com esta antecedência (estudo: "vence em 5 dias"). */
export const WORKOUT_EXPIRY_NOTICE_DAYS = 5;
/** Tantos dias sem treinar registrado e o aluno aparece como sumido (estudo: "6 dias sem treinar"). */
export const WORKOUT_GONE_DAYS = 6;
export const WORKOUT_WATCH_FLAGS = ['vencida', 'vence', 'sumido', 'em_dia'];
/** Uma linha do "Para acompanhar" da mesa do personal. */
export const workoutWatchRowSchema = z.object({
    patientId: z.uuid(),
    name: z.string(),
    flag: z.enum(WORKOUT_WATCH_FLAGS),
    planId: z.uuid(),
    planTitle: z.string(),
    validUntil: isoDate.nullable(),
    /** Dias até o fim da ficha (negativo: venceu há tantos dias). */
    daysLeft: z.number().int().nullable(),
    /** A ficha do próximo ciclo já programada: o dia em que entra. */
    upcomingStartsOn: isoDate.nullable(),
    /**
     * Dias desde o último treino (registrado no app ou sessão com presença); sem treino nenhum,
     * desde o começo da ficha. Nulo: a ficha ainda não começou.
     */
    daysSince: z.number().int().nullable(),
    /** Ainda não treinou nenhuma vez com esta ficha. */
    neverTrained: z.boolean().optional(),
    /** Dias com treino nesta semana (desde segunda) e o combinado da ficha. */
    weekDone: z.number().int(),
    perWeek: z.number().int().nullable(),
    /** Telefone para o "Chamar" (só o número, para o WhatsApp). */
    phone: z.string().nullable().optional(),
});
